/**
 * API Configuration
 * Centralized configuration for all API endpoints using environment variables
 */

// Get API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: `${API_BASE_URL}/auth/login`,
    REGISTER: `${API_BASE_URL}/auth/register`,
    FORGOT_PASSWORD: `${API_BASE_URL}/auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/auth/reset-password`,
  },
  
  // User management endpoints
  USERS: {
    LIST: `${API_BASE_URL}/users`,
    CREATE: `${API_BASE_URL}/users`,
    UPDATE: (id: string) => `${API_BASE_URL}/users/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/users/${id}`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/users/${id}`,
  },
  
  // Asset management endpoints (for future use)
  ASSETS: {
    LIST: `${API_BASE_URL}/assets`,
    CREATE: `${API_BASE_URL}/assets`,
    UPDATE: (id: string) => `${API_BASE_URL}/assets/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/assets/${id}`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/assets/${id}`,
  },
  
  // Rental management endpoints (for future use)
  RENTALS: {
    LIST: `${API_BASE_URL}/rentals`,
    CREATE: `${API_BASE_URL}/rentals`,
    UPDATE: (id: string) => `${API_BASE_URL}/rentals/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/rentals/${id}`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/rentals/${id}`,
  },
  
  // Amenities management endpoints (for future use)
  AMENITIES: {
    LIST: `${API_BASE_URL}/amenities`,
    CREATE: `${API_BASE_URL}/amenities`,
    UPDATE: (id: string) => `${API_BASE_URL}/amenities/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/amenities/${id}`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/amenities/${id}`,
  },
  
  // Courses management endpoints
  COURSES: {
    LIST: `${API_BASE_URL}/courses`,
    CREATE: `${API_BASE_URL}/courses`,
    UPDATE: (id: string) => `${API_BASE_URL}/courses/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/courses/${id}`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/courses/${id}`,
  },
  
  // Lessons management endpoints
  LESSONS: {
    LIST: `${API_BASE_URL}/lessons`,
    CREATE: `${API_BASE_URL}/lessons`,
    UPDATE: (id: string) => `${API_BASE_URL}/lessons/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/lessons/${id}`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/lessons/${id}`,
    GET_BY_COURSE: (courseId: string) => `${API_BASE_URL}/lessons?course=${courseId}`,
  },
  
  // Presale management endpoints
  PRESALE: {
    LIST: `${API_BASE_URL}/presale`,
    CREATE: `${API_BASE_URL}/presale`,
    UPDATE: (id: string) => `${API_BASE_URL}/presale/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/presale/${id}`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/presale/${id}`,
  },
  
  // Investor applications (/raise investor verification form)
  INVESTOR_INQUIRIES: {
    LIST: `${API_BASE_URL}/investor-inquiries`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/investor-inquiries/${id}`,
  },

  // Partnership assessments (/tokenize 4-step questionnaire)
  TOKENIZE_ASSESSMENTS: {
    LIST: `${API_BASE_URL}/tokenize-assessments`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/tokenize-assessments/${id}`,
  },

  // Contacts management endpoints
  CONTACTS: {
    LIST: `${API_BASE_URL}/contacts`,
    CREATE: `${API_BASE_URL}/contacts`,
    UPDATE: (id: string) => `${API_BASE_URL}/contacts/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/contacts/${id}`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/contacts/${id}`,
  },
  
    // Partners management endpoints
    PARTNERS: {
      LIST: `${API_BASE_URL}/partners`,
      CREATE: `${API_BASE_URL}/partners`,
      UPDATE: (id: string) => `${API_BASE_URL}/partners/${id}`,
      DELETE: (id: string) => `${API_BASE_URL}/partners/${id}`,
      GET_BY_ID: (id: string) => `${API_BASE_URL}/partners/${id}`,
    },
    
    // Blog management endpoints
    BLOG: {
      LIST: `${API_BASE_URL}/blogs`,
      CREATE: `${API_BASE_URL}/blogs`,
      UPDATE: (id: string) => `${API_BASE_URL}/blogs/${id}`,
      DELETE: (id: string) => `${API_BASE_URL}/blogs/${id}`,
      GET_BY_ID: (id: string) => `${API_BASE_URL}/blogs/admin/${id}`,
      RESTORE: (id: string) => `${API_BASE_URL}/blogs/${id}/restore`,
    },
    
    // Testnet Signup endpoints
    TESTNET_SIGNUP: {
      LIST: `${API_BASE_URL}/testnet-signup`,
      GET_BY_ID: (id: string) => `${API_BASE_URL}/testnet-signup/${id}`,
    },
    
    // Pioneer Users management endpoints
    PIONEER_USERS: {
      LIST: `${API_BASE_URL}/pioneer-users`,
      GET_BY_ID: (id: string) => `${API_BASE_URL}/pioneer-users/${id}`,
      DELETE: (id: string) => `${API_BASE_URL}/pioneer-users/${id}`,
      RESTORE: (id: string) => `${API_BASE_URL}/pioneer-users/${id}/restore`,
    },

    // Newsletter (Stay Updated / footer) endpoints
    NEWSLETTER: {
      LIST: `${API_BASE_URL}/newsletter`,
      BROADCAST: `${API_BASE_URL}/newsletter/broadcast`,
    },
} as const;

// Helper function to get full API URL
export const getApiUrl = (endpoint: string): string => {
  return endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
};

// Environment information
export const ENV_CONFIG = {
  API_BASE_URL,
  NODE_ENV: process.env.NODE_ENV,
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_PRODUCTION: process.env.NODE_ENV === 'production',
} as const;

// Log current configuration in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Configuration:', {
    baseUrl: API_BASE_URL,
    environment: process.env.NODE_ENV,
    endpoints: API_ENDPOINTS,
  });
}
